/*
 * Authors: mariushirjanu
 * Created: 03/11/2022
 */

import * as _ from 'underscore';
import { util } from '../util';
import { GaEventService } from '../services/ga-event.service';
import { GaMessageService, IDialog, IFormErrors } from '../message/ga-message.service';
import { BreadcrumbService } from '../breadcrumbs/breadcrumb.service';
import { TranslationService } from '../translation/translation.service';
import { GaValidateService, IComponentValidationResult, IValidatingComponentConfig, IValidationResults } from '../formly/ga-validate.service';
import { StateService, Transition, TransitionService } from '@uirouter/angular';
import { IObject } from '../../../type_definitions/datatypes';
import { Type, ViewContainerRef } from '@angular/core';
import { StateDeclaration } from '@uirouter/core';

type OnBeforeStepChangeFn = () => boolean | Promise<boolean>;

export interface IWizardStep {
  id:string;
  title:string;
  titleTranslateValues:string;
  component:Type<any>;
  state:string;
  stateParams?:any;
  stateChildren?:string[];
  enabled:boolean;
  reason?:string;
  hide?:boolean;
  index?:number;
  onBeforeStepChange?:OnBeforeStepChangeFn;
}

export interface IWizardAction {
  id:string;
  hidden?:boolean;
  disabled?:boolean;
  preventDisable?:boolean;
}

export interface IWizardButton extends IWizardAction {
  label:string;
  classes:string | string[];
  click:Function;
  busy?:boolean;
  iconClasses?:string;
  iconVisible?:boolean;
  buttons?:IWizardButton[];
}

export enum WizardEvent {
  ACTIONS_CHANGED = 'ga2.wizardActionsChanged',
  STEPS_CHANGED = 'ga2.wizardChanged',
  STEP_SELECTION_CHANGED = 'ga2.wizardStep',
  TITLE_CHANGED = 'ga2.wizardTitleChanged',
  SYNC = 'ga2.wizardSynced'
}

/**
 * @ngdoc class
 * @name GaWizard
 *
 * This class will listen to state changes to sync the active step and possibly prevent a state change
 * (if the corresponding step is disabled).
 *
 * If a state change is prevented, it will show a message to the user, using the 'reason' property.
 *
 * When the list of steps, or the state of any step changes, the Wizard will $broadcast a 'ga2.wizardChanged.{instanceId}'
 * event, containing the current list of steps.
 */
export class GaWizard {
  private _steps:IWizardStep[] = [];
  private _activeStepIndex:number = 0;
  private _skipValidation:boolean;
  private _stateSynced:boolean = true;
  private _started:boolean;
  private _currentValidator:Function;
  private _currentBeforeValidation:Function;
  private _currentValidatorStepId:string; // the wizard step id of the current validator
  private _actions:IWizardButton[] = [];
  private _title:string;
  private _shadowValidationTemplate:ViewContainerRef;
  private _hasSidebarNavigation:boolean;

  private removeOnStateStartListener:Function;
  private removeOnStateSuccessListener:Function;

  constructor(
    private readonly _instanceId:string,
    private breadcrumbService:BreadcrumbService,
    private stateService:StateService,
    private gaEventService:GaEventService,
    private messageService:GaMessageService,
    private translationService:TranslationService,
    private validateService:GaValidateService,
    private transitionService:TransitionService
  ) {
    if (!this.instanceId) {
      throw new ReferenceError('Instance id is required');
    }
    // starting up wizard
    this.startIfNecessary();
  }

  public get instanceId():string {
    return this._instanceId;
  }

  public get hasSidebarNavigation():boolean {
    return this._hasSidebarNavigation;
  }

  public set hasSidebarNavigation(hasSidebarNavigation:boolean) {
    this._hasSidebarNavigation = hasSidebarNavigation;
  }

  /**
   * @ngdoc method
   * @name GaWizard#step
   * @methodOf Wizard
   * @description Updates or simply returns a step by id.
   *
   * @param {Number} stepId       the id of the step to update or return
   * @param {Object=} step   the step object to put in the step list (if not specified, the step list is not updated)
   * @returns {Object}        the step with the specified id
   */
  public step(stepId:string, step?:IWizardStep):IWizardStep {
    const idx:number = _.findIndex(this._steps, (s:IWizardStep) => s.id === stepId);
    if (idx === -1) {
      throw new ReferenceError(`Unknown step ${stepId}`);
    }

    if (arguments.length > 1) {
      this._steps[ idx ] = step;
      this.onStepsChange();
    }

    return this._steps[ idx ];
  }

  public steps(steps?:IWizardStep[]):IWizardStep[] {
    if (arguments.length) {
      this._steps = steps;
      this.onStepsChange();
    }
    return this._steps;
  }

  public visibleSteps():IWizardStep[] {
    return _.filter(this._steps, (step:IWizardStep) => !step.hide);
  }

  public activeStepId():string {
    const activeTab:IWizardStep = this.visibleSteps()[ this.activeStepIndex ];
    return activeTab?.id;
  }

  public activeStepTitle():string {
    const activeTab:IWizardStep = this.visibleSteps()[ this.activeStepIndex ];
    return activeTab?.title;
  }

  /**
   * @ngdoc method
   * @name GaWizard#activeStepIndex
   * @methodOf Wizard
   * @description Retrieves the index of the currently active step
   *
   * @returns {Number} The index of the currently active step
   */
  public get activeStepIndex():number {
    return this._activeStepIndex;
  }
  public set activeStepIndex(activeStep:number) {
    this._activeStepIndex = activeStep;
    this.onStepSelectionChange();
  }

  /**
   * @description get and set title of wizard
   */
  public get title():string {
    return this._title;
  }

  public set title(title:string) {
    this._title = title;
    this.gaEventService.sendMessage(`${WizardEvent.TITLE_CHANGED}.${this.instanceId}`, title);
  }

  /**
   * @ngdoc method
   * @name GaWizard#selectStep
   * @methodOf Wizard
   * @description Tries to select a step by id or index based on parameter type. This will cause a state change if the step is enabled,
   *  or an error message dialog if the step is disabled.
   *
   * @param {string | number}      stepIdOrIndex  step id or the index of the step to select
   * @returns {void}
   */
  public selectStep(stepIdOrIndex:string | number):void {
    let step:IWizardStep;

    if (_.isString(stepIdOrIndex)) {
      step = _.findWhere(this.visibleSteps(), { id: stepIdOrIndex });
    } else if (_.isNumber(stepIdOrIndex)) {
      step = this.visibleSteps()[ stepIdOrIndex ];
    }

    if (step) {
      const currentStep:IWizardStep = _.findWhere(this.visibleSteps(), { id: this.activeStepId() });
      const isPreconditionError:boolean | Promise<boolean> = currentStep?.onBeforeStepChange && currentStep?.onBeforeStepChange();
      const changeStep = ():void => {
        if (step.enabled) {
          if (this.stateSynced) {
            this.stateService.go(step.state, step.stateParams)
              .then(() => {
                this.activeStepIndex = this.stepIndex(step.id);
                this.breadcrumbService.empty();
              })
              .catch(util.noop);
          } else {
            this.activeStepIndex = this.stepIndex(step.id);
            this.breadcrumbService.empty();
          }
        } else {
          this.showStepErrorMessage(step);
        }
      };

      if (util.isPromise(isPreconditionError)) {
        (<Promise<boolean>>isPreconditionError)
          .then((invalid:boolean) => {
            if (!invalid) {
              changeStep();
            }
          });
      } else {
        if (isPreconditionError) {
          return;
        }

        changeStep();
      }
    }
  }

  /**
   * @description prevent state change used in $stateChangeStart uiRouter event
   */
  // private preventStateChange = (event:IAngularEvent, toState:StateDeclaration, toParams, fromState:StateDeclaration):void => {
  private preventStateChange = (transition:Transition):void => {
    const step:IWizardStep = _.findWhere(this._steps, { state: transition.to().name });
    if (step && transition.to().name !== transition.from().name) {
      if (!step.enabled) {
        transition.abort();
        this.showStepErrorMessage(step);
      } else if (step.hide) {
        transition.abort();
        //go to another step
        const stepIndex:number = _.indexOf(this._steps, step);
        if (stepIndex > 0) { //previous if it exists
          this.selectStep(stepIndex - 1);
        } else if (stepIndex < (this._steps.length - 1)) { //next if it exists
          this.selectStep(stepIndex + 1);
        }
      } else if (this._currentValidator && !this._skipValidation) {
        transition.abort();
        //only change after current step validates successfully
        this.validateCurrent()
          .then(() => {
            //do the state change
            this._skipValidation = true;
            this.stateService.go(transition.to().name, transition.params('to'));
          }, () => {
            //validation failed
          });
      } else {
        this._skipValidation = false;
      }
    }
  };

  /**
   * @ngdoc method
   * @name GaWizard#sync
   * @methodOf Wizard
   * @description Looks at the current state and marks the corresponding step as active
   */
  public sync = ():void => {
    let stepIndex:number = _.findIndex(this.visibleSteps(), (s:IWizardStep) => {
      let isChild:boolean;
      if (s.stateChildren) {
        isChild = _.indexOf(s.stateChildren, this.stateService.current.name) !== -1;
      }
      return ((s.state === this.stateService.current.name) || isChild);
    });
    if (stepIndex === -1) {
      this.stateSynced = false;
      stepIndex = 0;
    } else {
      this.stateSynced = true;
    }
    this.activeStepIndex = stepIndex;
    this.onWizardSync();
  };

  /**
   * @ngdoc method
   * @name GaWizard#startIfNecessary
   * @methodOf Wizard
   * @description Register listener when wizard is not started, or re-initiate listener when wizard is cleared (leave wizard)
   */
  public startIfNecessary():void {
    // init wizard listener when wizard is not started
    if (!this._started) {
      this.removeOnStateStartListener = this.transitionService.onStart({}, this.preventStateChange);
      this.removeOnStateSuccessListener = this.transitionService.onSuccess({}, this.sync);
    }

    this._started = true;
  }

  public onBeforeStepsChange(fn:OnBeforeStepChangeFn):void {
    this.steps(_.map(this.steps(), (step:IWizardStep) => {
      step.onBeforeStepChange = fn;
      return step;
    }));
  }

  private showStepErrorMessage(step:IWizardStep):void {
    const title:string = this.translationService.instant('APP.WIZARD.STEPERROR', { steptitle: this.translationService.instant(step.title) });
    this.messageService.showDialog({
      title,
      message: step.reason || title,
      closeButton: true
    });
  }

  private onStepsChange():void {
    this.gaEventService.sendMessage(`${WizardEvent.STEPS_CHANGED}.${this.instanceId}`, this._steps);
  }

  private onStepSelectionChange():void {
    this.gaEventService.sendMessage(`${WizardEvent.STEP_SELECTION_CHANGED}.${this.instanceId}`, this._activeStepIndex);
  }

  private onActionsChange():void {
    this.gaEventService.sendMessage(`${WizardEvent.ACTIONS_CHANGED}.${this.instanceId}`, this._actions);
  }

  private onWizardSync():void {
    this.gaEventService.sendMessage(`${WizardEvent.SYNC}.${this.instanceId}`, this.stateSynced);
  }

  /**
   * @ngdoc method
   * @name GaWizard#enabled
   * @methodOf Wizard
   * @description Updates or retrieves the enabled/disabled status of a step by id.
   *
   * @param {Number} stepId           the id of the step
   * @param {Boolean=} isEnabled  the status to set (if not specified, the status is not updated)
   * @param {string=} reason     the reason to use in case of disabling the step
   * @returns {Boolean}           the enabled/disabled status of the step with the specified id
   */
  public enabled(stepId:string, isEnabled?:boolean, reason?:string):boolean {
    const step:IWizardStep = this.step(stepId);
    if (arguments.length > 1) {
      step.enabled = isEnabled;
      step.reason = reason;
      this.onStepsChange();
    }
    return step.enabled;
  }

  /**
   * @ngdoc method
   * @name GaWizard#hide
   * @methodOf Wizard
   * @description Updates or retrieves the hide status of a step by id.
   *
   * @param {Number} stepId           the id of the step
   * @param {Boolean=} hidden  the status to set (if not specified, the status is not updated)
   * @returns {Boolean}           the enabled/disabled status of the step with the specified id
   */
  public hide(stepId:string, hidden?:boolean):boolean {
    const step:IWizardStep = this.step(stepId);
    if (arguments.length > 1) {
      step.hide = hidden;
      this.onStepsChange();
    }
    return step.hide;
  }

  /**
   * @ngdoc method
   * @name GaWizard#toggleState
   * @methodOf Wizard
   * @description Toggle enabled status of steps
   *
   * @param {number|number[]} stepIndices           list of steps
   * @param {Boolean} posState  positive state
   * @param {Boolean} negState  negative state
   * @param {String=} posReason  positive reason
   * @param {String=} negReason  negative reason
   */
  private toggleState(stepIndices:number | number[], posState:boolean, negState:boolean, posReason?:string, negReason?:string):void {
    stepIndices = _.isArray(stepIndices)? stepIndices : [ stepIndices ];
    _.forEach(this._steps, (step:IWizardStep, idx:number) => {
      if (_.contains(<number[]>stepIndices, idx)) {
        step.enabled = posState;
        step.reason = posReason;
      } else {
        step.enabled = negState;
        step.reason = negReason;
      }
    });
    this.onStepsChange();
  }

  /**
   * @ngdoc method
   * @name GaWizard#someEnabled
   * @methodOf Wizard
   * @description Updates the status of some steps to enabled, the others will be disabled
   *
   * @param {number|number[]} toEnable   the (array of) index of the steps that should be enabled
   * @param {string=} reason             the reason to use for steps that will be disabled
   */
  public someEnabled(toEnable:number | number[], reason?:string):void {
    this.toggleState(toEnable, true, false, undefined, reason);
  }

  /**
   * @ngdoc method
   * @name GaWizard#someDisabled
   * @methodOf Wizard
   * @description Updates the status of some steps to disabled, the others will be enabled
   *
   * @param {number|number[]} toDisable  the (array of) index of the steps that should be disabled
   * @param {string=} reason             the reason to use for steps that will be disabled
   */
  public someDisabled(toDisable:number | number[], reason?:string):void {
    this.toggleState(toDisable, false, true, reason);
  }

  /**
   * @ngdoc method
   * @name GaWizard#othersDisabled
   * @methodOf Wizard
   * @description Disables all but the currently active step
   *
   * @param {string=} reason  The reason to use for steps that will be disabled
   */
  public othersDisabled(reason?:string):void {
    this.toggleState(_.without(_.range(this._steps.length), this.activeStepIndex), false, true, reason);
  }

  /**
   * @description get and set synced state of wizard properties
   */
  public get stateSynced():boolean {
    return this._stateSynced;
  }

  public set stateSynced(synced:boolean) {
    this._stateSynced = synced;
  }

  /**
   * @ngdoc method
   * @name ga2.common.service:wizardService#isDialog
   * @methodOf ga2.common.service:wizardService
   * @description Determine if the wizard was opened in a dialog (the active state is not matching any of the step states)
   *
   * @returns {boolean} true when the wizard is open in a dialog (only available after state synced)
   */
  public isInDialog():boolean {
    return !this._stateSynced;
  }

  /**
   * @ngdoc method
   * @name GaWizard#stepIndex
   * @methodOf Wizard
   * @description Return the index of a step id
   *
   * @param {string=} id  The id of the step
   */
  private stepIndex(id:string):number {
    const index:number = _.findIndex(this.visibleSteps(), { id });
    return index !== -1 ? index : 0;
  }

  /**
   * @ngdoc method
   * @name GaWizard#allEnabled
   * @methodOf Wizard
   * @description Changes the status of all steps to enabled
   */
  public allEnabled():void {
    this.toggleState(_.range(this._steps.length), true, false);
  }

  /**
   * @ngdoc method
   * @name Wizard#validateCurrent
   * @methodOf Wizard
   * @description Validates using the current step validator.
   *
   * When validation fails an error message is displayed to the user, allowing him/her to ignore the error.
   *
   * @returns {Promise}  a promise that will be resolved if validation passed (or the user selects ignore),
   *  or will be rejected if validation fails.
   */
  public validateCurrent(type?:string):Promise<any> {
    return new Promise((resolve, reject) => {
      if (!this._currentValidator) {
        return resolve(undefined);
      }

      const getStepErrors = (errorMessages:string[]):(string|IFormErrors)[] => {
        // add step title
        if(this._currentValidatorStepId){
          const step:IWizardStep = _.findWhere(this.steps(), { id: this._currentValidatorStepId });
          const title:string = step?.title;

          return [{
            title: this.translationService.instant(title),
            errors: errorMessages
          }];
        }

        return errorMessages;
      };

      const onValidationFinished = (errorMessages:string[]) => {
        if (!errorMessages || !errorMessages.length) {
          return resolve(undefined);
        } else {
          reject(errorMessages);
          const invalidFormDialog:IDialog = this.messageService.showInvalidFormMessage(
            'GA.VALIDATION.VALIDATION_FAILED',
            undefined,
            getStepErrors(errorMessages), () => {
              invalidFormDialog.hide();
            }
          );
        }
      };

      // execute any before validation function and wait for its promise to be resolved
      const onBeforeValidation = ():Promise<void> => {
        if(!this._currentBeforeValidation){
          return Promise.resolve();
        }
        return this._currentBeforeValidation();
      };

      onBeforeValidation()
        .then(() => {
          const result = this._currentValidator(type ? { type } : undefined);
          if (util.isPromise(result)) {
            result
              .then(onValidationFinished)
              .catch(onValidationFinished);
          } else {
            onValidationFinished(result);
          }
        });
    });
  }

  /**
   * @ngdoc method
   * @name GaWizard#validateAll
   * @methodOf Wizard
   * @description Validate a list of components.
   *
   * @param {string=} type      provide the type of the validation that is going to be passed to the validation component
   * @param {IObject=} inputs   containing the input variable passed to all the components (e.g.: orderId)
   *
   * @returns {Promise<IValidationResults|void>} validation errors
   */
  public validateAll(type?:string, inputs?:IObject):Promise<IValidationResults|void> {
    const componentsToValidate:IValidatingComponentConfig[] = [];

    _.each(this.visibleSteps(), (step:IWizardStep) => {
      if (step.enabled) {
        const sc:StateDeclaration = this.stateService.get(step.state);
        if (sc.data && ((type && _.contains(sc.data.validate, type)) || (!type && sc.data.validate))) {
          componentsToValidate.push({
            id: step.id,
            class: step.component
          });
        }
      }
    });

    return this.validateService.validateComponents(componentsToValidate, this.shadowValidationTemplate,type ? { type } : undefined, inputs)
      .catch((results:IValidationResults) => {
        const errors:(string|IFormErrors)[] = [];
        let goToStep:IWizardStep;
        _.each(results.componentResults, (compResults:IComponentValidationResult) => {
          if(compResults?.errors?.length) {
            const step:IWizardStep = _.findWhere(this.steps(), { id: compResults.id });
            const title:string = step?.title;

            errors.push({
              title: this.translationService.instant(title),
              errors: compResults.errors
            });

            if(util.isNullOrUndefined(goToStep)){
              goToStep = step;
            }
          }
        });

        const invalidFormDialog:IDialog = this.messageService.showInvalidFormMessage('GA.VALIDATION.VALIDATION_FAILED', undefined, errors, () => {
          setTimeout(() => {
            invalidFormDialog.hide();
            if (goToStep) {
              this.selectStep(goToStep.id);
            }
          }, 200);
        });

      return Promise.reject(results);
    });
  }

  /**
   * @ngdoc method
   * @name GaWizard#currentValidator
   * @methodOf Wizard
   * @description Sets the current step validator. This method must return a (promise of) an array of error messages.
   *
   * @param {Function} validatorFn A method that can be used for validation. It should return a promise or an array of error messages.
   * @param {Boolean=} skipValidation when specified, it will not validate on step changed
   * @param {Function=} beforeValidationFn A method that be used to wait until resolved, before executing validatorFn
   * @param {string=} validatorStepId The step id of the current validator
   */
  public currentValidator(validatorFn:Function, skipValidation?:boolean, beforeValidationFn?:Function, validatorStepId?:string):void {
    this._currentValidator = validatorFn;
    this._skipValidation = skipValidation;
    this._currentBeforeValidation = beforeValidationFn;
    this._currentValidatorStepId = validatorStepId;
  }

  /**
   * @ngdoc method
   * @name GaWizard#actions
   * @methodOf Wizard
   * @description Updates or retrieves the list of action objects
   *
   * @param {IWizardButton[]=} actions   the list of action objects to set. When not specified, the list is not updated.
   * @returns {IWizardButton[]}        the list of action objects
   */
  public actions(actions?:IWizardButton[]):IWizardButton[] {
    if (arguments.length) {
      this._actions = actions;
      this.onActionsChange();
    }
    return this._actions;
  }

  /**
   * @ngdoc method
   * @name GaWizard#addAction
   * @methodOf GaWizard
   * @description adds an action to the list. If the actionId already exists, the action is not added.
   *
   * @param {IWizardButton=} action       the action to be added
   * @param {boolean} endOfList          when true, the action is appended to the end of the list, otherwise it's added to the beginning
   */
  public addAction(action:IWizardButton, endOfList?:boolean):void {
    if (this.findAction(action.id)) {
      return;
    }

    if (endOfList) {
      this._actions.push(action);
    } else {
      this._actions.unshift(action);
    }
    this.actions(this._actions); // this will make sure that a notification is sent
  }

  /**
   * @ngdoc method
   * @name GaWizard#removeAction
   * @methodOf GaWizard
   * @description removes an action from the list by id. If the actionId already exists, it's ignored.
   *
   * @param {String=} actionId       the id of the action
   */
  public removeAction(actionId:string):void {
    // we first check that it exists, because we don't want to send a notification if it's already there
    if (!this.findAction(actionId)) {
      return;
    }

    const filteredActions = this._actions.filter(a => a.id !== actionId);
    this.actions(filteredActions); // this will make sure that a notification is sent
  }

  /**
   * @ngdoc method
   * @name GaWizard#setActionVisible
   * @methodOf Wizard
   * @description Updates the hidden status of an action by id.
   *
   * @param {String} actionId       the id of the action
   * @param {Boolean} visible       set action visibility status
   */
  public setActionVisible(actionId:string, visible:boolean):void {
    (this.findAction(actionId) || <any>{}).hidden = !visible;
    this.onActionsChange();
  }

  /**
   * @ngdoc method
   * @name GaWizard#setActionIconVisible
   * @methodOf Wizard
   * @description Updates the iconVisible status of an action by id.
   *
   * @param {String} actionId       the id of the action
   * @param {Boolean} visible       set icon visibility status (true/false)
   */
  public setActionIconVisible(actionId:string, visible:boolean):void {
    (this.findAction(actionId) || <any>{}).iconVisible = visible;
    this.onActionsChange();
  }

  /**
   * @ngdoc method
   * @name GaWizard#updateActionStatus
   * @methodOf Wizard
   * @description Updates the busy status and  of an action by id.
   *
   * @param {String} actionId     the id of the action
   * @param {Boolean} executing   set busy status and disability status of action (true/false)
   */
  public updateActionStatus(actionId:string, executing:boolean):void {
    this.setActionBusy(actionId, executing);
    this.setActionEnabled(actionId, !executing);
  }

  /**
   * @ngdoc method
   * @name GaWizard#setActionEnabled
   * @methodOf Wizard
   * @description Updates the disability status of an action by id.
   *
   * @param {String} actionId           the id of the action
   * @param {Boolean} enabled           set disability status of action (true/false)
   */
  public setActionEnabled(actionId:string, enabled:boolean):void {
    (this.findAction(actionId) || <any>{}).disabled = !enabled;
  }

  /**
   * @ngdoc method
   * @name GaWizard#setActionBusy
   * @methodOf Wizard
   * @description Updates the busy status of an action by id.
   *
   * @param {String} actionId   the id of the action
   * @param {Boolean} busy      set busy status of action (true/false)
   */
  public setActionBusy(actionId:string, busy:boolean):void {
    (this.findAction(actionId) || <any>{}).busy = busy;
  }

  /**
   * @ngdoc method
   * @name GaWizard#setActionClasses
   * @methodOf Wizard
   * @description Updates the busy status of an action by id.
   *
   * @param {string} actionId   the id of the action
   * @param {string|string[]} classes    action classes
   */
  public setActionClasses(actionId:string, classes:string|string[]):void {
    (this.findAction(actionId) || <any>{}).classes = classes;
  }

  /**
   * @ngdoc method
   * @name GaWizard#findAction
   * @methodOf Wizard
   * @description Find action by ID
   *
   * @param {String} id           the id of the action
   * @return {IWizardAction}      Object of wizard action
   */
  private findAction(id:string):IWizardAction {
    return _.findWhere(this._actions, { id });
  }

  public set shadowValidationTemplate(shadowValidationTemplate:ViewContainerRef) {
    this._shadowValidationTemplate = shadowValidationTemplate;
  }

  public get shadowValidationTemplate():ViewContainerRef {
    return this._shadowValidationTemplate;
  }

  /**
   * @ngdoc method
   * @name GaWizard#clear
   * @methodOf Wizard
   * @description Removes all steps
   */
  public clear():void {
    // destroy event
    this.removeOnStateStartListener();
    this.removeOnStateSuccessListener();

    this._steps = [];
    this._actions = [];
    this.activeStepIndex = 0;
    this.stateSynced = true;
    this._started = false;
    this.hasSidebarNavigation = undefined;

    // notify about changes
    this.onStepsChange();
    this.onActionsChange();
  }

  public clearActions():void {
    this._actions = [];
    this.onActionsChange();
  }

  public clearCurrentValidator():void {
    this._currentValidator = undefined;
    this._currentBeforeValidation = undefined;
    this._currentValidatorStepId = undefined;
  }
}